/**
 * @file Profile data managed via TanStack Query.
 *
 * Provides query options and mutations for profile management.
 * Profiles represent creator identities within an organization.
 */

import type { QueryClient } from "@tanstack/react-query";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { trpcClient } from "../trpc";
import { changeLogKeys } from "./change-log";
import { profileDetailsKeys } from "./profile-details";
import {
  invalidatePublicProfilePayloads,
  publicProfileKeys,
} from "./public-profile";

// ============================================================================
// Query Keys
// ============================================================================

export const profileKeys = {
  all: ["profile"] as const,
  lists: () => [...profileKeys.all, "list"] as const,
  listByOrg: (organizationId: string) =>
    [...profileKeys.lists(), organizationId] as const,
  details: () => [...profileKeys.all, "detail"] as const,
  detail: (organizationId: string, profileId: string) =>
    [...profileKeys.details(), organizationId, profileId] as const,
  bySlugByOrg: (organizationId: string) =>
    [...profileKeys.all, "bySlug", organizationId] as const,
  bySlug: (organizationId: string, slug: string) =>
    [...profileKeys.bySlugByOrg(organizationId), slug] as const,
  resolveSlugByOrg: (organizationId: string) =>
    [...profileKeys.all, "resolveSlug", organizationId] as const,
  resolveSlug: (organizationId: string, slug: string) =>
    [...profileKeys.resolveSlugByOrg(organizationId), slug] as const,
  availableEmails: (organizationId: string) =>
    [...profileKeys.all, "availableEmails", organizationId] as const,
  changeLog: (organizationId: string, profileId: string) =>
    changeLogKeys.profile(organizationId, profileId),
};

// ============================================================================
// Types
// ============================================================================

export interface Profile {
  id: string;
  organizationId: string;
  firstName: string | null;
  lastName: string | null;
  name: string;
  slug: string;
  description: string | null;
  avatarUrl: string | null;
  metadata: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  email: string | null;
  emailVerified: boolean | null;
  emailVerifiedAt: Date | null;
  emailSource: string | null;
  createdAt: Date;
  updatedAt: Date;
  addresses: Array<{
    id: string;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    line1: string | null;
    line2: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
    isPrimary: boolean;
  }>;
  payments: Array<{
    type: "paypal";
    email: string | null;
    username: string | null;
  }>;
}

export interface AvailableEmail {
  email: string;
  source: string;
  isUserEmail: boolean;
}

// ============================================================================
// Query Options
// ============================================================================

/**
 * Query options for fetching profiles by organization ID.
 */
export function profileListQueryOptions(organizationId: string) {
  return queryOptions({
    queryKey: profileKeys.listByOrg(organizationId),
    queryFn: () =>
      trpcClient.organization.profiles.list.query({ organizationId }),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

/**
 * Retry a profile query up to twice, but never on 404/403: a missing or
 * forbidden profile won't appear on retry, and the slug routes act on the 404.
 */
function retryUnlessForbiddenOrMissing(
  failureCount: number,
  error: Error,
): boolean {
  const status = (error as { data?: { httpStatus?: number } })?.data
    ?.httpStatus;
  if (status === 404 || status === 403) return false;
  return failureCount < 2;
}

/**
 * Query options for fetching a specific profile.
 */
export function profileQueryOptions(organizationId: string, profileId: string) {
  return queryOptions({
    queryKey: profileKeys.detail(organizationId, profileId),
    queryFn: () =>
      trpcClient.organization.profiles.get.query({ organizationId, profileId }),
    enabled: !!profileId, // Only fetch if profileId is not empty
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: retryUnlessForbiddenOrMissing,
  });
}

/**
 * Query options for fetching a profile by slug.
 */
export function profileBySlugQueryOptions(
  organizationId: string,
  slug: string,
) {
  return queryOptions({
    queryKey: profileKeys.bySlug(organizationId, slug),
    queryFn: () =>
      trpcClient.organization.profiles.getBySlug.query({
        organizationId,
        slug,
      }),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: retryUnlessForbiddenOrMissing,
  });
}

/**
 * Query options for resolving a profile slug to the profile's current slug
 * (EPD-11640). A retired slug resolves to the new one; NOT_FOUND otherwise.
 * Used by the slug-keyed layout routes to redirect old links.
 */
export function profileSlugResolveQueryOptions(
  organizationId: string,
  slug: string,
) {
  return queryOptions({
    queryKey: profileKeys.resolveSlug(organizationId, slug),
    queryFn: () =>
      trpcClient.organization.profiles.resolveSlug.query({
        organizationId,
        slug,
      }),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: retryUnlessForbiddenOrMissing,
  });
}

/**
 * Query options for fetching available emails for profile assignment.
 */
export function availableEmailsQueryOptions(organizationId: string) {
  return queryOptions({
    queryKey: profileKeys.availableEmails(organizationId),
    queryFn: () =>
      trpcClient.organization.profiles.getAvailableEmails.query({
        organizationId,
      }),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook for fetching profiles by organization ID.
 */
export function useProfileListQuery(organizationId: string) {
  return useQuery(profileListQueryOptions(organizationId));
}

/**
 * Mutation to ensure the current user has a profile in the organization.
 * If no profile exists, the server auto-creates one from the auth identity.
 * On success, invalidates the profile list so queries pick up the new profile.
 */
export function useEnsureProfile(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      trpcClient.organization.profiles.ensureProfile.mutate({
        organizationId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: profileKeys.listByOrg(organizationId),
      });
    },
  });
}

/**
 * Suspense hook for fetching profiles by organization ID.
 */
export function useSuspenseProfileList(organizationId: string) {
  return useSuspenseQuery(profileListQueryOptions(organizationId));
}

/**
 * Hook for fetching a specific profile.
 */
export function useProfileQuery(organizationId: string, profileId: string) {
  return useQuery(profileQueryOptions(organizationId, profileId));
}

/**
 * Suspense hook for fetching a profile by slug.
 */
export function useSuspenseProfileBySlug(organizationId: string, slug: string) {
  return useSuspenseQuery(profileBySlugQueryOptions(organizationId, slug));
}

/**
 * Hook for fetching available emails for profile assignment.
 */
export function useAvailableEmailsQuery(organizationId: string) {
  return useQuery(availableEmailsQueryOptions(organizationId));
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook for creating a new profile.
 */
export function useCreateProfile(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      name: string;
      slug: string;
      description?: string;
      avatarUrl?: string;
      metadata?: string;
      email?: string;
    }) =>
      trpcClient.organization.profiles.create.mutate({
        organizationId,
        ...data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: profileKeys.listByOrg(organizationId),
      });
    },
  });
}

/**
 * Hook for requesting a cross-org profile claim.
 * Sends a confirmation email to the profile owner — the profile is not moved
 * until the owner clicks the link in the email.
 */
export function useRequestProfileClaim(organizationId: string) {
  return useMutation({
    mutationFn: ({ callerEmail }: { callerEmail: string }) =>
      trpcClient.organization.profiles.requestProfileClaim.mutate({
        organizationId,
        callerEmail,
      }),
  });
}

/**
 * The profile's slug as currently cached (detail or by-slug query), or
 * undefined when the profile isn't in the cache.
 */
function cachedProfileSlug(
  queryClient: QueryClient,
  organizationId: string,
  profileId: string,
): string | undefined {
  const detail = queryClient.getQueryData(
    profileQueryOptions(organizationId, profileId).queryKey,
  );
  if (detail) return detail.slug;
  const bySlug = queryClient.getQueriesData<CachedProfile>({
    queryKey: profileKeys.bySlugByOrg(organizationId),
  });
  return bySlug.find(([, profile]) => profile?.id === profileId)?.[1]?.slug;
}

type CachedProfile = Awaited<
  ReturnType<typeof trpcClient.organization.profiles.getBySlug.query>
>;

/**
 * Hook for updating a profile.
 */
export function useUpdateProfile(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      profileId: string;
      firstName?: string;
      lastName?: string;
      name?: string;
      slug?: string;
      description?: string;
      avatarUrl?: string | null;
      metadata?: string;
      // Media kit (public): URL or uploaded file. fileName/fileSize null when URL.
      mediaKitUrl?: string | null;
      mediaKitFileName?: string | null;
      mediaKitFileSize?: number | null;
      // Profile location (separate from shipping addresses)
      city?: string;
      state?: string;
      country?: string;
      // EPD-4209
      address?: {
        firstName?: string;
        lastName?: string;
        phone?: string;
        line1?: string;
        line2?: string;
        city?: string;
        state?: string;
        zip?: string;
        country?: string;
      };
      payment?: {
        type: "paypal";
        email?: string;
        username?: string;
      };
    }) =>
      trpcClient.organization.profiles.update.mutate({
        organizationId,
        ...data,
      }),
    // Capture the slug before the update so onSuccess can tell a real slug
    // change from an unchanged slug sent alongside other fields.
    onMutate: (variables) => ({
      previousSlug: cachedProfileSlug(
        queryClient,
        organizationId,
        variables.profileId,
      ),
    }),
    onSuccess: (updated, variables, context) => {
      // The server no-ops an unchanged slug, so only a slug that differs from
      // the pre-update one re-keys the slug lookups. When the previous slug
      // isn't cached, assume it changed: refetching an old slug would 404.
      const slugChanged =
        variables.slug !== undefined &&
        (context?.previousSlug === undefined ||
          updated.slug !== context.previousSlug);
      if (slugChanged) {
        // EPD-11640: a slug change re-keys every slug lookup. Mark them stale
        // without refetching (the old slug would 404 under a mounted observer);
        // the next route load refetches and redirects old slugs.
        for (const queryKey of [
          profileKeys.bySlugByOrg(organizationId),
          profileKeys.resolveSlugByOrg(organizationId),
          publicProfileKeys.all,
        ]) {
          queryClient.invalidateQueries({ queryKey, refetchType: "none" });
        }
      } else {
        // EPD-12133: the Toolkit preview and public pages read the profile
        // through the slug-keyed public queries, which nothing else refreshes
        // — without this a name/bio/avatar/location edit stayed hidden behind
        // their staleTime until a hard refresh.
        invalidatePublicProfilePayloads(queryClient);
      }
      queryClient.invalidateQueries({
        queryKey: profileKeys.detail(organizationId, variables.profileId),
      });
      // The Profile tab (EPD-12592) shows the name, bio, photo and media kit.
      queryClient.invalidateQueries({
        queryKey: profileDetailsKeys.byProfile(
          organizationId,
          variables.profileId,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: profileKeys.listByOrg(organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: changeLogKeys.profile(organizationId, variables.profileId),
      });
    },
  });
}

/**
 * Hook for deleting a profile.
 * Optionally reassigns the profile's legacy email to another profile.
 */
export function useDeleteProfile(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      profileId: string;
      reassignEmailToProfileId?: string;
    }) =>
      trpcClient.organization.profiles.delete.mutate({
        organizationId,
        profileId: data.profileId,
        reassignEmailToProfileId: data.reassignEmailToProfileId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.all });
    },
  });
}

/**
 * Hook for setting a profile's email address.
 * Triggers verification email to be sent (or auto-verifies if email is pre-verified).
 */
export function useSetProfileEmail(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { profileId: string; email: string }) =>
      trpcClient.organization.profiles.setEmail.mutate({
        organizationId,
        ...data,
      }),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({
        queryKey: profileKeys.detail(organizationId, variables.profileId),
      });
      queryClient.invalidateQueries({
        queryKey: profileKeys.listByOrg(organizationId),
      });
      // Invalidate available emails when auto-verified (new verified email may be added)
      if (result.status === "auto_verified") {
        queryClient.invalidateQueries({
          queryKey: profileKeys.availableEmails(organizationId),
        });
      }
    },
  });
}

/**
 * Hook for adding a profile address.
 */
export function useAddProfileAddress(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      profileId: string;
      address: {
        firstName?: string;
        lastName?: string;
        phone?: string;
        line1?: string;
        line2?: string;
        city?: string;
        state?: string;
        zip?: string;
        country?: string;
        isPrimary?: boolean;
      };
    }) =>
      trpcClient.organization.profiles.addAddress.mutate({
        organizationId,
        ...data,
      }),
    onSuccess: (result, variables) => {
      // The primary address is the public location (EPD-12133).
      invalidatePublicProfilePayloads(queryClient);
      queryClient.invalidateQueries({
        queryKey: profileKeys.detail(organizationId, variables.profileId),
      });
      // Also invalidate lists as address count/presence might change
      queryClient.invalidateQueries({
        queryKey: profileKeys.listByOrg(organizationId),
      });
      // Invalidate change-log to reflect the new address
      queryClient.invalidateQueries({
        queryKey: profileKeys.changeLog(organizationId, variables.profileId),
      });
    },
  });
}

/**
 * Hook for updating a profile address.
 */
export function useUpdateProfileAddress(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      profileId: string;
      addressId: string;
      address: {
        firstName?: string;
        lastName?: string;
        phone?: string;
        line1?: string;
        line2?: string;
        city?: string;
        state?: string;
        zip?: string;
        country?: string;
        isPrimary?: boolean;
      };
    }) =>
      trpcClient.organization.profiles.updateAddress.mutate({
        organizationId,
        ...data,
      }),
    onSuccess: (result, variables) => {
      // The primary address is the public location (EPD-12133).
      invalidatePublicProfilePayloads(queryClient);
      queryClient.invalidateQueries({
        queryKey: profileKeys.detail(organizationId, variables.profileId),
      });
      // Invalidate change-log to reflect the update
      queryClient.invalidateQueries({
        queryKey: profileKeys.changeLog(organizationId, variables.profileId),
      });
    },
  });
}

/**
 * Hook for deleting a profile address.
 */
export function useDeleteProfileAddress(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { profileId: string; addressId: string }) =>
      trpcClient.organization.profiles.deleteAddress.mutate({
        organizationId,
        ...data,
      }),
    onSuccess: (result, variables) => {
      // The primary address is the public location (EPD-12133).
      invalidatePublicProfilePayloads(queryClient);
      queryClient.invalidateQueries({
        queryKey: profileKeys.detail(organizationId, variables.profileId),
      });
      queryClient.invalidateQueries({
        queryKey: profileKeys.listByOrg(organizationId),
      });
      // Invalidate change-log to reflect deletion
      queryClient.invalidateQueries({
        queryKey: profileKeys.changeLog(organizationId, variables.profileId),
      });
    },
  });
}

// ============================================================================
// Cache Utilities
// ============================================================================

/**
 * Prefetch profiles for an organization.
 * Useful in route loaders.
 */
export async function prefetchProfileList(
  queryClient: QueryClient,
  organizationId: string,
) {
  return queryClient.fetchQuery(profileListQueryOptions(organizationId));
}

/**
 * Prefetch a profile by slug.
 */
export async function prefetchProfileBySlug(
  queryClient: QueryClient,
  organizationId: string,
  slug: string,
) {
  return queryClient.fetchQuery(
    profileBySlugQueryOptions(organizationId, slug),
  );
}

/**
 * Invalidate all profile queries.
 */
export function invalidateProfileQueries(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: profileKeys.all });
}
